import { useState, useEffect } from "react";
import {
  Card, Table, Stack, TableRow, TableBody, TableCell, Container, Typography,
  TableContainer, Chip, CircularProgress, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, Switch, FormControlLabel,
  List, ListItem, ListItemAvatar, ListItemText, Avatar, Divider, Box,
} from "@mui/material";
import Page from "src/components/Page";
import Scrollbar from "src/components/Scrollbar";
import SearchNotFound from "src/components/SearchNotFound";
import ListHead from "src/components/common/ListHead";
import ListToolbar from "src/components/common/ListToolbar";
import MyPagination from "src/components/common/MyPagination";
import { asyncRequest } from "src/services/network";
import { alert } from "src/services/alert";
import Confirm from "src/components/common/ConfirmDialog";

const TABLE_HEAD = [
  { id: "sort_order", label: "排序", alignRight: false, width: 60 },
  { id: "name", label: "场次名称", alignRight: false, width: 120 },
  { id: "time_range", label: "时间段", alignRight: false, width: 180 },
  { id: "duration", label: "时长(分钟)", alignRight: false, width: 100 },
  { id: "products", label: "商品数", alignRight: false, width: 80 },
  { id: "status", label: "状态", alignRight: false, width: 80 },
  { id: "is_default", label: "默认", alignRight: false, width: 60 },
  { id: "action", label: "操作", alignRight: false, width: 240 },
];

const DEFAULT_FORM = {
  name: "",
  start_hour: 14,
  start_minute: 0,
  duration_minutes: 60,
  sort_order: 0,
  status: 1,
};

const IMG_BASE = "https://oss.jingshixc.com";

function getImgUrl(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${IMG_BASE}/${path}`;
}

export default function FlashSaleSessionList() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasQueried, setHasQueried] = useState(false);
  const [filter, setFilter] = useState({ page: 0, size: 20, status: "" });

  // 编辑场次弹窗
  const [editOpen, setEditOpen] = useState(false);
  const [editMode, setEditMode] = useState("create");
  const [editInitial, setEditInitial] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  // 配置商品弹窗
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [productSessionId, setProductSessionId] = useState(null);
  const [productSessionName, setProductSessionName] = useState("");
  const [allProducts, setAllProducts] = useState([]);        // sectionId=6 的所有商品
  const [productConfig, setProductConfig] = useState({});     // { productId: { enabled, flash_stock, flash_price } }
  const [productLoading, setProductLoading] = useState(false);
  const [productSaving, setProductSaving] = useState(false);

  async function getItems(filterOverride) {
    setItems(null);
    const q = filterOverride || filter;
    const payload = { page: q.page, size: q.size };
    if (q.status !== "") payload.status = Number(q.status);
    const { data, count } = await asyncRequest({ path: "/flash-sale/session/list", data: payload });
    setItems(data || []);
    setTotal(count || 0);
    setHasQueried(true);
  }

  const handlePageChange = (event, value, newSize) => {
    let newFilter;
    if (newSize !== undefined) {
      newFilter = { ...filter, page: 0, size: newSize };
    } else {
      const newPage = value - 1;
      if (newPage === filter.page) return;
      newFilter = { ...filter, page: newPage };
    }
    setFilter(newFilter);
    if (hasQueried) getItems(newFilter);
  };

  function handleReset() {
    setHasQueried(false);
    setItems(null);
    setTotal(0);
    setFilter({ page: 0, size: 20, status: "" });
  }

  function openCreate() {
    setEditMode("create");
    setEditInitial(null);
    setForm(DEFAULT_FORM);
    setEditOpen(true);
  }

  function openEdit(row) {
    setEditMode("edit");
    setEditInitial(row);
    setForm({
      name: row.name || "",
      start_hour: row.start_hour ?? 14,
      start_minute: row.start_minute ?? 0,
      duration_minutes: row.duration_minutes ?? 60,
      sort_order: row.sort_order ?? 0,
      status: row.status ?? 1,
    });
    setEditOpen(true);
  }

  async function handleSave() {
    if (!form.name) return alert("请输入场次名称");
    setSaving(true);
    try {
      if (editMode === "create") {
        await asyncRequest({ path: "/flash-sale/session/create", data: form });
        alert("创建成功", "success");
      } else {
        await asyncRequest({ path: "/flash-sale/session/update", data: { id: editInitial.id, ...form } });
        alert("更新成功", "success");
      }
      setEditOpen(false);
      getItems();
    } catch (e) {
      /* error handled by interceptor */
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleStatus(row) {
    const next = row.status === 1 ? 0 : 1;
    await asyncRequest({
      path: "/flash-sale/session/toggle-status",
      data: { id: row.id, status: next },
    });
    alert(next === 1 ? "已启用" : "已禁用", "success");
    getItems();
  }

  async function handleDelete(row) {
    if (row.is_default === 1) return alert("默认场次不可删除，只能禁用");
    Confirm({
      message: `确认删除场次「${row.name}」？关联商品配置也会被删除。`,
      async onConfirm() {
        try {
          const res = await asyncRequest({ path: "/flash-sale/session/delete", data: { id: row.id } });
          if (res.code === 1) { alert("删除成功", "success"); getItems(); }
          else alert(res.msg || "删除失败");
        } catch (err) { alert("删除失败"); }
      },
    });
  }

  async function handleReloadStock() {
    try {
      const { msg } = await asyncRequest({ path: "/flash-sale/stock/reload", data: {} });
      alert(msg || "库存缓存已刷新", "success");
    } catch (e) {}
  }

  // ======================== 配置商品弹窗 ========================

  async function openProductDialog(row) {
    setProductSessionId(row.id);
    setProductSessionName(row.name);
    setProductDialogOpen(true);
    setProductLoading(true);
    setAllProducts([]);
    setProductConfig({});

    try {
      // 并行：获取秒杀专区所有商品 + 该场次已配置的商品
      const [storeRes, sessionRes] = await Promise.all([
        asyncRequest({ path: "/store/product/list", data: { sectionId: 6, page: 0, size: 200 } }),
        asyncRequest({ path: "/flash-sale/session-product/list", data: { session_id: row.id } }),
      ]);

      const storeProducts = (storeRes && storeRes.data) || [];
      const configuredList = (sessionRes && sessionRes.data) || [];

      // 已配置商品按 product_id 索引（物理删除后只有最新记录）
      const configMap = {};
      configuredList.forEach(sp => {
        configMap[sp.product_id] = {
          enabled: true,
          flash_stock: sp.flash_stock !== undefined && sp.flash_stock !== null ? sp.flash_stock : 10,
          flash_price: sp.flash_price || 0,
          sort_order: sp.sort_order || 0,
          limit_per_user: sp.limit_per_user || 1,
        };
      });

      setAllProducts(storeProducts);
      // 初始化配置：已配置的默认开启，未配置的默认关闭
      const initConfig = {};
      storeProducts.forEach(p => {
        if (configMap[p.id]) {
          initConfig[p.id] = configMap[p.id];
        } else {
          initConfig[p.id] = { enabled: false, flash_stock: 10, flash_price: p.price || 0, sort_order: 0, limit_per_user: 1 };
        }
      });
      setProductConfig(initConfig);
    } catch (e) {
      alert("加载商品数据失败");
    } finally {
      setProductLoading(false);
    }
  }

  function toggleProduct(productId) {
    setProductConfig(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        enabled: !prev[productId]?.enabled,
      },
    }));
  }

  function updateProductField(productId, field, value) {
    setProductConfig(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: value,
      },
    }));
  }

  async function handleSaveProducts() {
    setProductSaving(true);
    try {
      const products = [];
      allProducts.forEach(p => {
        const cfg = productConfig[p.id];
        if (cfg && cfg.enabled) {
          const stock = Number(cfg.flash_stock);
          products.push({
            product_id: p.id,
            flash_price: Number(p.price) || 0,
            flash_stock: !isNaN(stock) && stock >= 0 ? stock : 10,  // 允许0库存
            limit_per_user: Number(cfg.limit_per_user) || 1,
            sort_order: Number(cfg.sort_order) || 0,
            status: 1,
          });
        }
      });

      await asyncRequest({
        path: "/flash-sale/session-product/save",
        data: { session_id: productSessionId, products },
      });
      alert("保存成功", "success");
      setProductDialogOpen(false);
      getItems();
    } catch (e) {
      /* error handled */
    } finally {
      setProductSaving(false);
    }
  }

  function fmtTime(h, m, dur) {
    const pad = (n) => String(n ?? 0).padStart(2, "0");
    const sH = Number(h || 0), sM = Number(m || 0), durMin = Number(dur || 0);
    const totalStart = sH * 60 + sM;
    const totalEnd = totalStart + durMin;
    const eH = Math.floor(totalEnd / 60) % 24, eM = totalEnd % 60;
    return `${pad(sH)}:${pad(sM)} ~ ${pad(eH)}:${pad(eM)}`;
  }

  const enabledCount = Object.values(productConfig).filter(c => c.enabled).length;
  const isNotFound = items && items.length === 0;

  return (
    <Page title="秒杀场次管理">
      <Container maxWidth="2000px">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>秒杀场次管理</Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={handleReloadStock}>刷新库存缓存</Button>
            <Button variant="contained" onClick={openCreate}>新建场次</Button>
          </Stack>
        </Stack>

        <Card>
          <ListToolbar showSearch={false} onReset={handleReset} onRefresh={() => getItems()}>
            <Grid container rowGap={2} alignItems="center">
              <Grid item sm={6} md={3}>
                <select
                  value={filter.status}
                  onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                  style={{ padding: "8px 12px", borderRadius: 4, border: "1px solid #ccc" }}
                >
                  <option value="">全部状态</option>
                  <option value="1">启用</option>
                  <option value="0">禁用</option>
                </select>
              </Grid>
              <Grid item sm={6} md={3}>
                <Button variant="contained" size="small" onClick={() => getItems()}>查询</Button>
              </Grid>
            </Grid>
          </ListToolbar>

          <Scrollbar>
            <TableContainer>
              <Table>
                <ListHead headLabel={TABLE_HEAD} />
                {!hasQueried ? (
                  <TableBody>
                    <TableRow><TableCell colSpan={8}>
                      <Stack sx={{ width: "100%", height: 200 }} alignItems="center" justifyContent="center">
                        <Typography variant="body2" color="text.secondary">请点击查询加载数据</Typography>
                      </Stack>
                    </TableCell></TableRow>
                  </TableBody>
                ) : !items ? (
                  <TableBody>
                    <TableRow><TableCell colSpan={8}>
                      <Stack sx={{ width: "100%", height: 200 }} alignItems="center" justifyContent="center">
                        <CircularProgress />
                      </Stack>
                    </TableCell></TableRow>
                  </TableBody>
                ) : (
                  <TableBody>
                    {items.map((row) => (
                      <TableRow hover key={row.id} tabIndex={-1}>
                        <TableCell>{row.sort_order}</TableCell>
                        <TableCell><Typography fontWeight={600}>{row.name}</Typography></TableCell>
                        <TableCell>{fmtTime(row.start_hour, row.start_minute, row.duration_minutes)}</TableCell>
                        <TableCell>{row.duration_minutes}</TableCell>
                        <TableCell>
                          <Chip size="small" label={`${row.active_product_count || 0}/${row.product_count || 0}`}
                            color={row.active_product_count > 0 ? "primary" : "default"} />
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={row.status === 1 ? "启用" : "禁用"}
                            color={row.status === 1 ? "success" : "default"} />
                        </TableCell>
                        <TableCell>
                          {row.is_default === 1 && <Chip size="small" label="默认" color="warning" />}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Button size="small" variant="contained" color="primary"
                              onClick={() => openProductDialog(row)}>配置商品</Button>
                            <Button size="small" variant="outlined" onClick={() => openEdit(row)}>编辑</Button>
                            <Button size="small" variant="outlined"
                              color={row.status === 1 ? "warning" : "success"}
                              onClick={() => handleToggleStatus(row)}>
                              {row.status === 1 ? "禁用" : "启用"}
                            </Button>
                            {row.is_default !== 1 && (
                              <Button size="small" variant="outlined" color="error"
                                onClick={() => handleDelete(row)}>删除</Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                )}
                {isNotFound && (
                  <TableBody>
                    <TableRow><TableCell align="center" colSpan={8} sx={{ py: 5 }}><SearchNotFound /></TableCell></TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <MyPagination total={total} page={filter.page} size={filter.size} onChange={handlePageChange} />
        </Card>
      </Container>

      {/* 编辑场次弹窗 */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode === "create" ? "新建秒杀场次" : "编辑秒杀场次"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="场次名称" placeholder="如：14:00场"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="开始小时 (0-23)"
                inputProps={{ min: 0, max: 23 }}
                value={form.start_hour}
                onChange={(e) => setForm({ ...form, start_hour: parseInt(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="开始分钟 (0-59)"
                inputProps={{ min: 0, max: 59 }}
                value={form.start_minute}
                onChange={(e) => setForm({ ...form, start_minute: parseInt(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="持续时长(分钟)"
                inputProps={{ min: 1 }}
                value={form.duration_minutes}
                onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="排序序号"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} />
            </Grid>
          </Grid>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            时间预览：{fmtTime(form.start_hour, form.start_minute, form.duration_minutes)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>取消</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "保存中..." : "保存"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 配置商品弹窗 */}
      <Dialog open={productDialogOpen} onClose={() => setProductDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          配置秒杀商品 - {productSessionName}
          <Typography variant="body2" color="text.secondary" component="span">
            已选 {enabledCount} 件商品
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {productLoading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>加载秒杀专区商品...</Typography>
            </Stack>
          ) : allProducts.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
              <Typography variant="body2" color="text.secondary">
                秒杀专区（sectionId=6）暂无商品，请先在商品管理中创建
              </Typography>
            </Stack>
          ) : (
            <List disablePadding>
              {allProducts.map((p, idx) => {
                const cfg = productConfig[p.id] || { enabled: false, flash_stock: 10, flash_price: 0 };
                return (
                  <Box key={p.id}>
                    {idx > 0 && <Divider />}
                    <ListItem sx={{ px: 2, py: 1.5 }}>
                      <ListItemAvatar>
                        <Avatar
                          variant="rounded"
                          src={getImgUrl(p.cover)}
                          sx={{ width: 56, height: 56 }}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={p.name}
                        secondary={`售价 ¥${p.price || '-'}  |  库存 ${p.stock || '-'}`}
                        primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
                        secondaryTypographyProps={{ fontSize: 12 }}
                        sx={{ ml: 1.5, flex: 1 }}
                      />
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Typography variant="body2" color="text.secondary" sx={{ width: 80, textAlign: 'center' }}>
                          ¥{p.price || '-'}
                        </Typography>
                        <TextField
                          label="库存"
                          type="number"
                          size="small"
                          sx={{ width: 80 }}
                          inputProps={{ min: 0 }}
                          value={cfg.flash_stock}
                          disabled={!cfg.enabled}
                          onChange={(e) => updateProductField(p.id, "flash_stock", e.target.value)}
                        />
                        <TextField
                          label="限购"
                          type="number"
                          size="small"
                          sx={{ width: 70 }}
                          inputProps={{ min: 1 }}
                          value={cfg.limit_per_user}
                          disabled={!cfg.enabled}
                          onChange={(e) => updateProductField(p.id, "limit_per_user", e.target.value)}
                        />
                        <TextField
                          label="排序"
                          type="number"
                          size="small"
                          sx={{ width: 70 }}
                          inputProps={{ min: 0 }}
                          value={cfg.sort_order}
                          disabled={!cfg.enabled}
                          onChange={(e) => updateProductField(p.id, "sort_order", e.target.value)}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={cfg.enabled}
                              onChange={() => toggleProduct(p.id)}
                              color="primary"
                            />
                          }
                          label={cfg.enabled ? "启用" : "关闭"}
                          sx={{ mr: 0, minWidth: 80 }}
                        />
                      </Stack>
                    </ListItem>
                  </Box>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductDialogOpen(false)}>取消</Button>
          <Button variant="contained" onClick={handleSaveProducts}
            disabled={productSaving || productLoading}>
            {productSaving ? "保存中..." : `保存 (${enabledCount}件)`}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
